import emailjs from '@emailjs/browser';
import { supabase } from '../lib/supabase';

class FeedbackService {
  static async saveFeedback(feedbackData) {
    try {
      // First, try to save to Supabase
      console.log('Saving feedback to Supabase:', feedbackData);
      const { data, error } = await supabase
        .from('feedback')
        .insert([
          {
            rating: feedbackData.rating,
            comments: feedbackData.feedback,
            patient_id: feedbackData.patientId,
            appointment_id: feedbackData.appointmentId,
            source: feedbackData.source || 'website',
            created_at: new Date().toISOString()
          }
        ])
        .select();
      
      if (error) {
        console.error('Error saving feedback to Supabase:', error);
        throw error;
      }
      
      console.log('Successfully saved feedback to Supabase:', data);
      
      // Send email notification
      try {
        await emailjs.send(
          'tebra',
          'template_c3z3ewg',
          {
            feedback_rating: feedbackData.rating,
            feedback_text: feedbackData.feedback,
            timestamp: new Date().toLocaleString(),
            to_email: 'info@outstanding.agency',
            subject: `New Private Feedback (${feedbackData.rating} stars)`
          }
        );
      } catch (emailError) {
        console.error('Error sending feedback notification:', emailError);
      }
      
      return data[0];
    } catch (error) {
      console.error('Error in saveFeedback:', error);
      
      // Fall back to localStorage if Supabase fails
      console.log('Falling back to localStorage for feedback storage');
      const feedbacks = JSON.parse(localStorage.getItem('feedbacks') || '[]');
      const newFeedback = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...feedbackData
      };
      feedbacks.push(newFeedback);
      localStorage.setItem('feedbacks', JSON.stringify(feedbacks));
      
      return newFeedback;
    }
  }

  static async getFeedbacks(options = {}) {
    try {
      // First try to get from Supabase
      console.log('Fetching feedback from Supabase with options:', options);
      
      let query = supabase
        .from('feedback')
        .select('*');
      
      // Apply filters if provided
      if (options.rating) {
        query = query.eq('rating', options.rating);
      }
      
      if (options.patientId) {
        query = query.eq('patient_id', options.patientId);
      }
      
      if (options.source) {
        query = query.eq('source', options.source);
      }
      
      // Apply sorting - default to newest first
      const sortField = options.sortField || 'created_at';
      const sortOrder = options.sortOrder || { ascending: false };
      query = query.order(sortField, sortOrder);
      
      // Use proper pagination with range
      // Default to a high limit (10000) but allow it to be configured
      const limit = options.limit || 10000;
      const from = options.from || 0;
      const to = from + limit - 1;
      query = query.range(from, to);
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching feedback from Supabase:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error in getFeedbacks:', error);
      
      // Fall back to localStorage
      console.log('Falling back to localStorage for feedback retrieval');
      return JSON.parse(localStorage.getItem('feedbacks') || '[]');
    }
  }
  
  static async getTopRatedFeedback(limit = 5) {
    try {
      // Get only 5-star public feedback from Supabase
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('rating', 5)
        .eq('is_public', true) // Only get public feedback
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) {
        console.error('Error fetching top rated feedback:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error in getTopRatedFeedback:', error);
      
      // Fall back to localStorage filtering
      const allFeedback = JSON.parse(localStorage.getItem('feedbacks') || '[]');
      return allFeedback
        .filter(f => f.rating === 5)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);
    }
  }

  static async getFeedbackStats(options = { publicOnly: true }) {
    try {
      // Try RPC function first (most efficient - single query with aggregation)
      if (options.publicOnly) {
        const { data, error } = await supabase.rpc('get_public_feedback_stats');
        
        if (!error && data) {
          console.log("[FeedbackService] Stats from RPC:", data);
          return {
            averageRating: parseFloat(data.averageRating) || 0,
            totalReviews: data.totalReviews || 0,
            distributionByRating: data.distributionByRating || {1: 0, 2: 0, 3: 0, 4: 0, 5: 0}
          };
        }
        
        // Fallback to count query if RPC not available
        console.log("[FeedbackService] RPC not available, using count query");
        const { count, error: countError } = await supabase
          .from('feedback')
          .select('rating', { count: 'exact', head: false })
          .eq('is_public', true);
        
        if (countError) throw countError;
        
        // Get average using a smaller sample if RPC fails
        const { data: sampleData } = await supabase
          .from('feedback')
          .select('rating')
          .eq('is_public', true)
          .limit(1000);
        
        const totalRating = (sampleData || []).reduce((sum, item) => sum + (item.rating || 0), 0);
        const averageRating = sampleData?.length ? parseFloat((totalRating / sampleData.length).toFixed(1)) : 0;
        
        return {
          averageRating,
          totalReviews: count || 0,
          distributionByRating: {1: 0, 2: 0, 3: 0, 4: 0, 5: 0}
        };
      } else {
        // Non-public stats - use traditional approach
        const allFeedback = await this.getFeedbacks({ limit: 10000 });
        
        if (!allFeedback || allFeedback.length === 0) {
          return {
            averageRating: 0,
            totalReviews: 0,
            distributionByRating: {1: 0, 2: 0, 3: 0, 4: 0, 5: 0}
          };
        }
        
        const totalRating = allFeedback.reduce((sum, item) => sum + (item.rating || 0), 0);
        const averageRating = parseFloat((totalRating / allFeedback.length).toFixed(1));
        
        const distributionByRating = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0};
        allFeedback.forEach(item => {
          if (item.rating >= 1 && item.rating <= 5) {
            distributionByRating[item.rating]++;
          }
        });
        
        return { averageRating, totalReviews: allFeedback.length, distributionByRating };
      }
    } catch (error) {
      console.error('Error in getFeedbackStats:', error);
      return {
        averageRating: 0,
        totalReviews: 0,
        distributionByRating: {1: 0, 2: 0, 3: 0, 4: 0, 5: 0}
      };
    }
  }

  /**
   * Logs when a feedback request is sent to a patient after appointment checkout
   * @param {Object} requestData - Data about the feedback request
   * @param {string} requestData.patientId - Patient ID from Athena Health
   * @param {string} requestData.appointmentId - Appointment ID from Athena Health
   * @param {boolean} requestData.emailSent - Whether the email was successfully sent
   * @param {string} requestData.sentAt - ISO timestamp when the request was sent
   * @returns {Promise<Object>} The created feedback request record
   */
  static async logFeedbackRequest(requestData) {
    try {
      console.log('Logging feedback request:', requestData);
      
      // First, try to save to Supabase
      const { data, error } = await supabase
        .from('feedback_requests')
        .insert([
          {
            patient_id: requestData.patientId,
            appointment_id: requestData.appointmentId,
            email_sent: requestData.emailSent,
            sent_at: requestData.sentAt,
            created_at: new Date().toISOString()
          }
        ])
        .select();
      
      if (error) {
        console.error('Error logging feedback request to Supabase:', error);
        throw error;
      }
      
      console.log('Successfully logged feedback request to Supabase:', data);
      return data[0];
    } catch (error) {
      console.error('Error in logFeedbackRequest:', error);
      
      // Fall back to localStorage if Supabase fails
      console.log('Falling back to localStorage for feedback request logging');
      const feedbackRequests = JSON.parse(localStorage.getItem('feedback_requests') || '[]');
      const newRequest = {
        id: Date.now(),
        patient_id: requestData.patientId,
        appointment_id: requestData.appointmentId,
        email_sent: requestData.emailSent,
        sent_at: requestData.sentAt,
        created_at: new Date().toISOString()
      };
      feedbackRequests.push(newRequest);
      localStorage.setItem('feedback_requests', JSON.stringify(feedbackRequests));
      
      return newRequest;
    }
  }
}

export default FeedbackService;
